// apps/roleforge-app/src/components/audit/AuditExperienceShell.tsx
// Plan 3b-ii — Phase 2.2: Outlet shell for the audit experience.
// Plan 3e-i — Phase 2.4: StepIndicator moved into AuditPersistentShell header.
//
// Issue #1228 — `GET /audits/:id` returns the audit AND its full, unpaginated
// transcript. This shell was throwing everything except `audit` away, and the
// converse page underneath then requested the identical payload again, so
// opening an audit downloaded the whole transcript twice. The first response
// is now published on the outlet context and the page hydrates from it.

import { useEffect, useState, useCallback } from 'react';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { getAudit, type AuditRow } from '../../lib/api-client';
import { useAuth } from '../../lib/auth';

export type AuditDetail = Awaited<ReturnType<typeof getAudit>>;

export interface AuditExperienceContext {
  audit: AuditRow;
  refreshAudit: () => Promise<void>;
  /**
   * The full `GET /audits/:id` payload from this shell's own load. Consumers
   * hydrate from it instead of re-requesting the transcript. It is null only
   * when a consumer is rendered outside this shell.
   */
  auditDetail: AuditDetail | null;
}

export function AuditExperienceShell() {
  const { id } = useParams<{ id: string }>();
  const { jwt } = useAuth();

  const [audit, setAudit] = useState<AuditRow | null>(null);
  const [auditDetail, setAuditDetail] = useState<AuditDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAudit = useCallback(async () => {
    if (!jwt || !id) return;
    try {
      const res = await getAudit(jwt, id);
      setAudit(res.audit);
      setAuditDetail(res);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit');
    } finally {
      setLoading(false);
    }
  }, [jwt, id]);

  const refreshAudit = useCallback(async () => {
    if (!jwt || !id) return;
    const res = await getAudit(jwt, id);
    setAudit(res.audit);
  }, [jwt, id]);

  useEffect(() => {
    void loadAudit();
  }, [loadAudit]);

  if (loading) {
    return <div className="p-8 text-graphite">Loading…</div>;
  }

  if (error || !audit) {
    return <div className="p-8 text-ember">{error ?? 'Audit not found.'}</div>;
  }

  if (audit.status === 'archived') {
    return <Navigate to={`/start?workspace=${encodeURIComponent(audit.workspace_id)}`} replace />;
  }

  return (
    <div className="flex-1 min-h-0">
      <Outlet
        context={{ audit, refreshAudit, auditDetail } satisfies AuditExperienceContext}
      />
    </div>
  );
}
